import {
  createRootLocationTransformer,
  defineHistoryService,
} from '@feature-hub/history-service';
import {defineLogger} from '@feature-hub/logger';
import {serializedStateManagerDefinition} from '@feature-hub/serialized-state-manager';
import {defineDisclaimerManager} from '@volkswagen-onehub/disclaimer-manager';
import {defineFeatureappService} from '@volkswagen-onehub/featureapp-service';
import {defineCarConfigurationService} from '@volkswagen-onehub/car-configuration-feature-service';

import {defineLocaleService} from '@volkswagen-onehub/locale-service';

import {defineServiceConfigProvider} from '@volkswagen-onehub/service-config-provider';

import {layerManagerDefinition} from '@volkswagen-onehub/layer-manager';

import {ModelOverviewIntegratorOptions} from './create-options-from-query-params';

import {faContentServiceDefinition} from '@volkswagen-onehub/fa-content-service';
import {fetchServiceConfigs} from './fetch-service-configs';
import {defineShowroomNavigationManager} from './mock-showroom-navigation-manager';
import {singleConfigurationFlowNavigatorDefinition} from '@volkswagen-onehub/single-configuration-flow-navigator';
import {defineNavigationService} from './mock-navigation-service';

// In this case we want to infer the type of the return value.
// tslint:disable-next-line:typedef
export async function defineCommonFeatureServices(
  options: ModelOverviewIntegratorOptions,
) {
  const {
    language,
    countryCode,
    currency,
    showroomNodeId,
    faServicesUrl,
    faServicesParams,
  } = options;

  const carConfigurationServiceDefinition = defineCarConfigurationService();

  const disclaimerManagerService = defineDisclaimerManager({
    legalEntityLabels: {master: 'Disclaimers by Volkswagen'},
    defaultLegalEntity: 'master',
  });

  const featureAppServiceDefinition = defineFeatureappService({
    'ulf': {
      name: 'ulf',
      baseUrl: 'https://v0-21-1.ulf.feature-app.io',
      src: '/assets/feature-app/user-location-feature-app.js',
      config: {},
    },
    'configure': {
      name: 'configure',
      baseUrl: 'https://v2-0-0.configure.feature-app.io',
      src: '/assets/feature-app/configure.js',
      css: [{href: '/assets/feature-app/configure.css'}],
      config: {applicationId: 'D6CONFIGURATOR', mode: 'standalone'},
    },
    'footer-disclaimers': {
      name: 'footer-disclaimers',
      baseUrl:
        'https://vwa-feature-app-footer-disclaimer.s3.amazonaws.com/1.2.0',
      src: 'index.umd.js',
    },
    'resolveMarketingCode': {
      name: 'resolveMarketingCode',
      baseUrl:
        'https://feature-app-resolve-marketing-code.s3.eu-central-1.amazonaws.com/v104.1',
      src: 'resolve-marketing-code.umd.js',
      config: {applicationId: 'RESOLVE_MARKETING_CODE'},
    },
    'uds': {
      name: 'uds',
      baseUrl: 'https://v0-25-0.uds.feature-app.io',
      src: 'assets/feature-app-my-configurations/my-configurations.js',
      config: {
        navigationModelList: {
          featureHubPageId: 'uds_my_saved_car_configurations',
          featureAppConfigurationId:
            'uds_my_saved_car_configurations_feature_app',
          pathname: '/',
        },
      },
    },
  });

  const localeServiceDefinition = defineLocaleService({
    language,
    countryCode,
    currency,
  });

  const showroomNavigationManagerDefinition = defineShowroomNavigationManager({
    currentShowroomNodeId: showroomNodeId,
  });

  const navigationService = defineNavigationService({
    primaryConsumerId: showroomNavigationManagerDefinition.id,
    pages: {
      'mofa-standalone': {
        path: '/standalone',
        featureApps: {'mofa-standalone': 'model-overview'},
      },
      'compare': {
        path: '/compare',
        featureApps: {compare: 'compare-feature-app'},
      },
    },
  });

  const historyServiceDefinition = defineHistoryService(
    createRootLocationTransformer({
      consumerPathsQueryParamName: '-',
      primaryConsumerId: showroomNavigationManagerDefinition.id,
    }),
    {mode: typeof window === 'undefined' ? 'static' : 'browser'},
  );

  const serviceConfigs = await fetchServiceConfigs({
    serviceURl: faServicesUrl ? faServicesUrl : '',
    endpoint: 'bff/mocks-config',
    params: faServicesParams,
  });

  const serviceConfigProviderDefinition = defineServiceConfigProvider({
    configs: {
      ...serviceConfigs.configs,
    },
  });

  const loggerDefinition = defineLogger((consumerId) => {
    const prefixArgs = [`%c${consumerId}`, 'font-weight: bold'];

    return {
      trace: console.trace.bind(console, ...prefixArgs),
      debug: console.debug.bind(console, ...prefixArgs),
      info: console.info.bind(console, ...prefixArgs),
      warn: console.warn.bind(console, ...prefixArgs),
      error: console.error.bind(console, ...prefixArgs),
    };
  });

  return {
    carConfigurationServiceDefinition,
    disclaimerManagerService,
    featureAppServiceDefinition,
    historyServiceDefinition,
    localeServiceDefinition,
    loggerDefinition,
    serializedStateManagerDefinition,
    serviceConfigProviderDefinition,
    showroomNavigationManagerDefinition,
    layerManagerDefinition,
    faContentServiceDefinition,
    singleConfigurationFlowNavigatorDefinition,
    navigationService,
  };
}
